"use client";

import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "utils";
import { Button } from "ui/components/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "ui/components/command";
import { Popover, PopoverContent, PopoverTrigger } from "ui/components/popover";
import Icon from "./icon";
import { LOADER2 } from "icons";

export function ComboboxDemo({
  data = [],
  value,
  onChange,
  search = "",
  onSearch,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  disabled = false,
  className,
  isLoading = false,
  icon,
  transformer,
  fetchFn,
  fetchParams = {},
  fetchKey = "id",
}) {
  const [open, setOpen] = React.useState(false);
  const [selectedItem, setSelectedItem] = React.useState(null);
  const [searchValue, setSearchValue] = React.useState(search);

  // Update search value when prop changes
  React.useEffect(() => {
    setSearchValue(search);
  }, [search]);

  // Handle value changes and fetch selected item if needed
  React.useEffect(() => {
    if (!value) {
      setSelectedItem(null);
      return;
    }

    // First try to find in current data
    const foundInData = data.find((item) => item.value === value);
    if (foundInData) {
      setSelectedItem(foundInData);
      return;
    }

    // If not found in data and we have a fetch function, fetch it automatically
    if (fetchFn && value) {
      const fetchSelectedItem = async () => {
        try {
          const fetchedItem = await fetchFn({
            [fetchKey]: value,
            ...fetchParams,
          });

          if (fetchedItem) {
            const transformedItem = transformer
              ? transformer(fetchedItem)
              : {
                  label: fetchedItem.name || fetchedItem.label,
                  value: fetchedItem[fetchKey] || fetchedItem.value,
                  select: fetchedItem.name || fetchedItem.label,
                };
            setSelectedItem(transformedItem);
          }
        } catch (error) {
          console.error("Error fetching selected item:", error);
          setSelectedItem(null);
        }
      };

      fetchSelectedItem();
    }
  }, [value, data, fetchFn, fetchParams, fetchKey, transformer]);

  // Handle search changes
  const handleSearchChange = (newSearchValue) => {
    setSearchValue(newSearchValue);
    onSearch?.(newSearchValue);
  };

  // Handle item selection
  const handleItemSelect = (currentValue) => {
    const selectedItemObject = data.find((item) => item.value === currentValue);

    const newSelectedItem =
      selectedItem?.value === currentValue ? null : selectedItemObject;

    if (newSelectedItem === null) {
      handleSearchChange("");
    }

    setSelectedItem(newSelectedItem);
    onChange?.(newSelectedItem);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      disabled={disabled}
      modal={true}
    >
      <PopoverTrigger asChild disabled={disabled}>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between",
            !selectedItem && "text-muted-foreground",
            className
          )}
        >
          <div className="flex items-center">
            {icon && (
              <Icon
                icon={icon}
                className={cn("mr-2", !selectedItem && "text-muted-foreground")}
              />
            )}
            <div
              className={cn(
                "text-left truncate max-w-40",
                !selectedItem && "text-muted-foreground"
              )}
            >
              {selectedItem ? selectedItem.select : placeholder}
            </div>
          </div>
          <ChevronsUpDown className="opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className={cn("p-0 !w-[var(--radix-popover-trigger-width)]")}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder}
            className="h-9"
            value={searchValue}
            onValueChange={handleSearchChange}
          />
          <CommandList>
            {isLoading ? (
              <div className="flex justify-center items-center p-4 text-muted-foreground">
                <Icon icon={LOADER2} className="animate-spin h-4 w-4 mr-2" />
                Loading...
              </div>
            ) : (
              <>
                <CommandGroup>
                  {data.map((item) => (
                    <CommandItem
                      key={item.value}
                      value={item.value}
                      onSelect={handleItemSelect}
                    >
                      {item.label}
                      <Check
                        className={cn(
                          "ml-auto",
                          selectedItem?.value === item.value
                            ? "opacity-100"
                            : "opacity-0"
                        )}
                      />
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandEmpty>No items found.</CommandEmpty>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
